import { useEffect, useState } from "react";
import { collection, query, orderBy, doc, updateDoc, serverTimestamp, writeBatch, Timestamp } from "firebase/firestore";
import { getDocs, getDocsFromCache, where } from "firebase/firestore";
import type { DocumentData, DocumentReference, Query, QuerySnapshot, WriteBatch } from "firebase/firestore";
import { createLocalDoc, fireWrite } from "../utils/offlineWrites";
import { getSegmentAndDescendantIds } from "../utils/segmentTree";
import { isCruiseSegment } from "../types";
import { onSnapshotResilient } from "../utils/resilientSnapshot";
import { db } from "../firebase";
import type { Segment, Person, CruiseDayType, SegmentType, CruiseConfig } from "../types";
import type { ItineraryReconciliation } from "../utils/cruiseItinerary";

export interface SegmentWithId extends Segment {
  id: string;
}

/** Real-time listener for all segments in a trip */
export function useSegments(tripId: string | undefined) {
  const [segments, setSegments] = useState<SegmentWithId[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tripId) {
      setSegments([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "trips", tripId, "segments"),
      orderBy("startDate", "asc"),
    );

    // Resilient: a segment list opened right after the trip was created races
    // the trip's own write to the server, and the rules deny it until the trip
    // doc lands. See `resilientSnapshot.ts`.
    const unsub = onSnapshotResilient(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as SegmentWithId[];
        setSegments(data);
        setLoading(false);
      },
      { label: `segments of trip ${tripId}`, onError: () => setLoading(false) },
    );

    return unsub;
  }, [tripId]);

  return { segments, loading };
}

/** Create a new segment */
export function createSegment(
  tripId: string,
  data: {
    name: string;
    parentSegmentId?: string | null;
    entityId: string | null;
    startDate: Date;
    endDate: Date;
    notes: string;
    /** Explicit "who was there"; null/omitted inherits from the parent/trip. */
    participants?: string[] | null;
    people?: Person[];
    segmentType?: SegmentType | null;
    cruiseConfig?: CruiseConfig | null;
    dayNumber?: number | null;
    dayType?: CruiseDayType | null;
    portName?: string | null;
    portEntityId?: string | null;
  },
): Promise<string> {
  const id = createLocalDoc(collection(db, "trips", tripId, "segments"), {
    name: data.name,
    parentSegmentId: data.parentSegmentId ?? null,
    entityId: data.entityId,
    startDate: Timestamp.fromDate(data.startDate),
    endDate: Timestamp.fromDate(data.endDate),
    participants: data.participants ?? null,
    people: data.people || [],
    notes: data.notes || null,
    customConfig: {},
    segmentType: data.segmentType ?? "general",
    cruiseConfig: data.cruiseConfig ?? null,
    dayNumber: data.dayNumber ?? null,
    dayType: data.dayType ?? null,
    portName: data.portName ?? null,
    portEntityId: data.portEntityId ?? null,
    createdAt: Timestamp.now(),
    updatedAt: serverTimestamp(),
  });
  return Promise.resolve(id);
}

/** Update an existing segment */
export function updateSegment(
  tripId: string,
  segmentId: string,
  data: Partial<{
    name: string;
    parentSegmentId: string | null;
    entityId: string | null;
    startDate: Date;
    endDate: Date;
    participants: string[] | null;
    people: Person[];
    notes: string | null;
    customConfig: Record<string, unknown>;
    segmentType: SegmentType;
    cruiseConfig: CruiseConfig | null;
    dayNumber: number | null;
    dayType: CruiseDayType | null;
    portName: string | null;
    portEntityId: string | null;
  }>,
): Promise<void> {
  const updates: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (data.name !== undefined) updates.name = data.name;
  if (data.parentSegmentId !== undefined) updates.parentSegmentId = data.parentSegmentId;
  if (data.entityId !== undefined) updates.entityId = data.entityId;
  if (data.startDate !== undefined) updates.startDate = Timestamp.fromDate(data.startDate);
  if (data.endDate !== undefined) updates.endDate = Timestamp.fromDate(data.endDate);
  if (data.participants !== undefined) updates.participants = data.participants;
  if (data.people !== undefined) updates.people = data.people;
  if (data.notes !== undefined) updates.notes = data.notes;
  if (data.customConfig !== undefined) updates.customConfig = data.customConfig;
  if (data.segmentType !== undefined) updates.segmentType = data.segmentType;
  if (data.cruiseConfig !== undefined) updates.cruiseConfig = data.cruiseConfig;
  if (data.dayNumber !== undefined) updates.dayNumber = data.dayNumber;
  if (data.dayType !== undefined) updates.dayType = data.dayType;
  if (data.portName !== undefined) updates.portName = data.portName;
  if (data.portEntityId !== undefined) updates.portEntityId = data.portEntityId;

  fireWrite(updateDoc(doc(db, "trips", tripId, "segments", segmentId), updates as Record<string, unknown> & { [x: string]: never }), "update segment");
  return Promise.resolve();
}

/** Firestore's cap on writes in one batch. */
const MAX_BATCH_WRITES = 500;

/**
 * Read a query from the local cache, falling back to a normal read only if
 * the cache itself can't answer. deleteSegment is reached from SegmentDetail,
 * which holds live listeners on both of the trip's segments and activities,
 * so the cache is as current as this device can be — and reading it can't
 * stall on a dead connection the way a server read can.
 */
async function readForDelete(q: Query): Promise<QuerySnapshot | null> {
  try {
    return await getDocsFromCache(q);
  } catch {
    try {
      return await getDocs(q);
    } catch {
      return null;
    }
  }
}

/**
 * Delete a segment, doing what SegmentDetail's confirmation promises:
 *
 *   - A cruise goes with its itinerary days (and anything else nested under
 *     it) and every activity filed under any of them. Deleting only the
 *     cruise doc used to leave its days and activities behind, invisible
 *     (nothing lists a segment whose parent is gone) but still in the data.
 *   - Any other segment goes alone. Its activities and the segments inside it
 *     move up a level, to whatever held the deleted one (or the trip itself).
 *     Leaving them pointing at the deleted segment would hide them: nothing
 *     lists an activity or a segment whose segment is gone.
 *
 * Written as batches committed with fireWrite, so it works offline and never
 * waits on the server. The segment itself is in the last batch: if an earlier
 * one is ever rejected, the segment is still there to delete again.
 */
export async function deleteSegment(tripId: string, segmentId: string): Promise<void> {
  const segmentsCol = collection(db, "trips", tripId, "segments");
  const [segmentSnap, interactionSnap] = await Promise.all([
    readForDelete(segmentsCol),
    readForDelete(query(collection(db, "interactions"), where("tripId", "==", tripId))),
  ]);

  const segments = (segmentSnap?.docs ?? []).map(
    (d) => ({ id: d.id, ...d.data() }) as SegmentWithId,
  );
  const target = segments.find((s) => s.id === segmentId);
  const cascade = isCruiseSegment(target);
  const segmentIds = cascade ? getSegmentAndDescendantIds(segments, segmentId) : [segmentId];
  const inDeleted = new Set(segmentIds);
  const upALevel = target?.parentSegmentId ?? null;

  // Moves go one write each: an update to a doc someone else deleted
  // meanwhile fails, and in a batch it would take the whole delete with it.
  const moves: { ref: DocumentReference; data: DocumentData }[] = [];
  const ops: ((batch: WriteBatch) => void)[] = [];
  for (const d of interactionSnap?.docs ?? []) {
    const filedUnder = (d.data() as { segmentId?: string | null }).segmentId;
    if (!filedUnder || !inDeleted.has(filedUnder)) continue;
    if (cascade) {
      ops.push((b) => b.delete(d.ref));
    } else {
      moves.push({ ref: d.ref, data: { segmentId: upALevel, updatedAt: serverTimestamp() } });
    }
  }
  if (!cascade) {
    for (const s of segments) {
      if (s.parentSegmentId !== segmentId) continue;
      moves.push({ ref: doc(segmentsCol, s.id), data: { parentSegmentId: upALevel, updatedAt: serverTimestamp() } });
    }
  }
  // Children before parents, the target itself last.
  for (const id of segmentIds.slice().reverse()) {
    ops.push((b) => b.delete(doc(segmentsCol, id)));
  }

  for (const m of moves) fireWrite(updateDoc(m.ref, m.data), "move out of deleted segment");
  for (let i = 0; i < ops.length; i += MAX_BATCH_WRITES) {
    const batch = writeBatch(db);
    for (const op of ops.slice(i, i + MAX_BATCH_WRITES)) op(batch);
    fireWrite(batch.commit(), cascade ? "delete cruise" : "delete segment");
  }
}

/**
 * Apply a generated cruise itinerary plan as a single batched Firestore write:
 * create new day-segments (as children of the cruise segment), refresh
 * uncustomized ones, and delete any surplus uncustomized trailing days.
 * Customized days (and their activities) are left untouched. `ontripdelete`
 * already cascades segments + interactions, so no extra cleanup is needed here.
 */
export function commitItineraryReconciliation(
  tripId: string,
  cruiseSegmentId: string,
  plan: ItineraryReconciliation,
): Promise<void> {
  const batch = writeBatch(db);
  const segmentsCol = collection(db, "trips", tripId, "segments");

  for (const draft of plan.toCreate) {
    const ref = doc(segmentsCol);
    batch.set(ref, {
      name: draft.name,
      parentSegmentId: cruiseSegmentId,
      entityId: null,
      startDate: Timestamp.fromDate(draft.date),
      endDate: Timestamp.fromDate(draft.date),
      participants: null,
      people: [],
      notes: null,
      customConfig: {},
      segmentType: "general",
      cruiseConfig: null,
      dayNumber: draft.dayNumber,
      dayType: draft.dayType,
      portName: null,
      portEntityId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  for (const { id, patch } of plan.toUpdate) {
    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() };
    if (patch.name !== undefined) updates.name = patch.name;
    if (patch.dayType !== undefined) updates.dayType = patch.dayType;
    if (patch.startDate !== undefined) updates.startDate = Timestamp.fromDate(patch.startDate);
    if (patch.endDate !== undefined) updates.endDate = Timestamp.fromDate(patch.endDate);
    batch.update(doc(segmentsCol, id), updates as Record<string, unknown> & { [x: string]: never });
  }

  for (const { id } of plan.toRemove) {
    batch.delete(doc(segmentsCol, id));
  }

  fireWrite(batch.commit(), "itinerary reconciliation");
  return Promise.resolve();
}
